import { ExclusionChip, IndeterminateCheckbox } from '@stern-ui/ui';
import { formatDate } from '@stern-ui/utilities';
import { createColumnHelper, HeaderContext } from '@tanstack/react-table';

import {
	columnDefExtended,
	DataTableObject,
	PersonDataObject,
	TableConfigType,
} from '@/components/PatientDataTable';
import { DETAILS_SIDE_PANEL } from '@/components/PatientDataTable/consts';
import { isUs } from '@/globalConsts';
import { formatScreeningStatus } from '@/utils/statusUtils';

import { ListPatientsNewChip } from '../components/Lists/ListPatientsNewChip';
import { CellContextExtended, CriteriaObject, SearchCriteriaObject, StudiesObject } from '../types';
import { renderHciNameStatus } from '../utils';

const columnHelper = createColumnHelper<DataTableObject>();

type displayArrayItemTypes = StudiesObject | SearchCriteriaObject | CriteriaObject;

type HeaderSelectAllHandler = {
	selectAllHandler: (event: React.MouseEvent<HTMLInputElement>) => void;
};

type HeaderContextExtended = HeaderContext<DataTableObject, string> & HeaderSelectAllHandler;

const renderStatusElement = (data: StudiesObject) => {
	const { Element } = formatScreeningStatus(data);
	return Element;
};

const renderConsentDate = (data: StudiesObject) => {
	return <>{formatDate(data.consent_date as string, isUs)}</>;
};

const renderHci = (data: StudiesObject) => {
	return renderHciNameStatus(data.hci_name, data.hci_active);
};

const displayArrayItem = (
	arr: displayArrayItemTypes[],
	accessor: string,
	formatFunction?: (data: StudiesObject) => React.ReactElement
) => {
	if (!arr || arr.length === 0) return <span>-</span>;
	return arr.length > 1
		? `+${arr.length}`
		: typeof formatFunction === 'function'
			? formatFunction(arr[0] as StudiesObject)
			: arr[0][accessor as keyof displayArrayItemTypes];
};

const patientListColumnsCheckbox = [
	columnHelper.display({
		id: 'select',
		header: (props) => (
			<IndeterminateCheckbox
				{...{
					checked: props.table.getIsAllPageRowsSelected(),
					indeterminate: props.table.getIsSomePageRowsSelected(),
					onChange: props.table.getToggleAllRowsSelectedHandler(),
					onClick: (props as HeaderContextExtended).selectAllHandler,
					variant: 'lg',
					showEdgeSpacing: true,
					dataTestId: 'patientList-selectAll',
				}}
			/>
		),
		cell: (props) => (
			<IndeterminateCheckbox
				{...{
					checked: props.row.getIsSelected(),
					indeterminate: props.row.getIsSomeSelected(),
					onChange: props.row.getToggleSelectedHandler(),
					onClick: (e) => {
						e.stopPropagation();
					},
					variant: 'lg',
					showEdgeSpacing: true,
				}}
			/>
		),
	}),
];

const patientListColumnsStart = [
	columnHelper.accessor(
		(row) => `${(row as PersonDataObject).first_name} ${(row as PersonDataObject).last_name}`,
		{
			id: 'name',
			header: () => 'Patient Name',
			isPii: true,
		} as columnDefExtended
	),
	columnHelper.display({
		id: 'umed_id',
		header: () => 'uMed Study ID',
		cell: (props) =>
			displayArrayItem(
				(props.row.original as PersonDataObject).studies,
				'patient_identifier'
			),
	}),
];

const patientListColumnsNew = ({ canUpdate }: { canUpdate: boolean }) => [
	columnHelper.accessor('is_new', {
		header: () => '',
		cell: (info) => {
			return info.getValue() ? (
				<ListPatientsNewChip info={info as CellContextExtended} canUpdate={canUpdate} />
			) : null;
		},
	}),
];

const patientListColumnExclusions = [
	columnHelper.accessor('exclusion_count', {
		id: 'exclusion_count',
		header: () => <span className='sr-only'>Exclusion status</span>,
		cell: (info) => {
			const count = info.getValue<number | undefined>();
			return count ? (
				<ExclusionChip
					exclusionCodeCount={count}
					dataTestId='exclusion-chip-patient-list'
				/>
			) : null;
		},
		excludeFromColumnSelector: true,
	} as columnDefExtended),
];

const patientListColumns = [
	columnHelper.accessor('birth_date', {
		id: 'birth_date',
		header: () => 'Date of birth',
		cell: (props) => {
			return formatDate(props.getValue() as string, isUs);
		},
		isPii: true,
	} as columnDefExtended),
	columnHelper.display({
		id: 'study_name',
		header: () => 'Study/Cohort',
		cell: (props) => displayArrayItem((props.row.original as PersonDataObject).studies, 'name'),
	}),
	columnHelper.display({
		id: 'consent_date',
		header: () => 'Consent date',
		cell: (props) =>
			displayArrayItem(
				(props.row.original as PersonDataObject).studies,
				'consent_date',
				renderConsentDate
			),
	}),
	columnHelper.display({
		id: 'status',
		header: () => 'Patient status',
		cell: (props) =>
			displayArrayItem(
				(props.row.original as PersonDataObject).studies,
				'status_label',
				renderStatusElement
			),
	}),
	columnHelper.display({
		id: 'hci_name',
		header: () => 'HCI',
		cell: (props) =>
			displayArrayItem(
				(props.row.original as PersonDataObject).studies,
				'hci_name',
				renderHci
			),
	}),
	columnHelper.display({
		id: 'search_criteria',
		header: () => 'Tagged search criteria',
		cell: (props) =>
			displayArrayItem((props.row.original as PersonDataObject).search_criteria, 'name'),
	}),
	columnHelper.accessor('gender', {
		id: 'gender',
		header: () => 'Gender',
		cell: (info) => {
			return <p className='capitalize'>{info.getValue() as string}</p>;
		},
		isPii: true,
	} as columnDefExtended),
	columnHelper.accessor('contact_info.last_contact_at', {
		header: () => 'Last contact at',
		id: 'last_contact_at',
		cell: (info) => {
			return formatDate(info.getValue() as string, isUs);
		},
	}),
	columnHelper.accessor(
		(row) =>
			`${(row as PersonDataObject).contact_info.last_contact_by_first_name} ${
				(row as PersonDataObject).contact_info.last_contact_by_last_name
			}`,
		{
			header: () => 'Last contact by name',
			id: 'last_contact_by_name',
		}
	),
	columnHelper.accessor('contact_info.number_of_contact_attempts', {
		id: 'number_of_contact_attempts',
		header: () => 'Number of followups',
	}),
	columnHelper.accessor('contact_info.contact_booked_for', {
		id: 'contact_booked_for',
		header: () => 'Call booked',
		cell: (info) => {
			return formatDate(info.getValue() as string, isUs);
		},
	}),
];

const patientListDynamicAdded = [
	columnHelper.accessor('added_at', {
		header: () => 'Date added',
		cell: (info) => {
			return formatDate(info.getValue() as string, isUs);
		},
		enableSorting: true,
	}),
];

const patientListDynamicRemoved = [
	columnHelper.accessor('removed_at', {
		header: () => 'Date removed',
		cell: (info) => {
			const val = info.getValue() as string | null;
			return val ? formatDate(val, isUs) : null;
		},
		enableSorting: true,
	}),
];

const patientListRowProps = {
	rowSelect: true,
	onRowClickTarget: {
		baseUrl: DETAILS_SIDE_PANEL,
		rowKey: (row: DataTableObject) =>
			`${(row as PersonDataObject).studies.find((study) => study.cancelled === 0)?.patient_id || 0}`,
	},
	getRowId: (row: DataTableObject) => (row as PersonDataObject).id,
};

const defaultPatientListColumnVisibility = {
	name: false,
	birth_date: false,
	contact_booked_for: false,
	last_contact_at: false,
	last_contact_by_name: false,
	number_of_contact_attempts: false,
};

export const patientListTableConfig: TableConfigType = {
	columns: [...patientListColumnsStart, ...patientListColumns],
	rowProps: patientListRowProps,
	visibleColumnDef: defaultPatientListColumnVisibility,
	defaultTabIndex: 0,
};

export const patientSearchListTableConfig: TableConfigType = isUs
	? patientListTableConfig
	: {
			...patientListTableConfig,
			columns: [
				...patientListColumnsStart,
				...patientListColumnExclusions,
				...patientListColumns,
			],
		};

export const patientListDynamicActiveNewTableConfig: TableConfigType = {
	columns: [
		...patientListColumnsCheckbox,
		...patientListColumnsStart,
		...patientListColumnsNew({ canUpdate: true }),
		...patientListColumns,
		...patientListDynamicAdded,
	],
	rowProps: { ...patientListRowProps, batchSelect: true },
	visibleColumnDef: { ...defaultPatientListColumnVisibility, gender: false },
	defaultTabIndex: 0,
};

export const patientListArchivedDynamicActiveNewTableConfig: TableConfigType = {
	columns: [
		...patientListColumnsStart,
		...patientListColumnsNew({ canUpdate: false }),
		...patientListColumns,
		...patientListDynamicAdded,
	],
	rowProps: { ...patientListRowProps },
	visibleColumnDef: { ...defaultPatientListColumnVisibility, gender: false },
	defaultTabIndex: 0,
};

export const patientListDynamicRemovedTableConfig: TableConfigType = {
	columns: [...patientListColumnsStart, ...patientListColumns, ...patientListDynamicRemoved],
	rowProps: patientListRowProps,
	visibleColumnDef: { ...defaultPatientListColumnVisibility, gender: false, status: false },
	defaultTabIndex: 0,
};
